import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getMe } from "lib/reducers/lookupData.selectors";
import { RemoteExecClient } from "lib/shell/ExecClient";
import { useDispatch, useSelector } from "store";
import { fetchStorageById, selectStorageByID } from "store/reducers";
import { useIsAdmin } from "components/Restricted";
import { DeviceShell } from "components/shared/features/deviceShell";

const StatusDot = ({ online }: { online: boolean }) => (
  <span
    aria-hidden
    className={`inline-block h-2 w-2 shrink-0 rounded-full ${online ? "bg-emerald-400" : "bg-red-400"}`}
  />
);

/**
 * DeviceShellPage is the full-page, admin-only command console for a toolbox. It is opened in a
 * new tab from the toolbox settings page and boots independently of the rest of the app shell.
 */
export const DeviceShellPage = () => {
  const dispatch = useDispatch();
  const { storageID = "" } = useParams<{ storageID: string }>();
  const isAdmin = useIsAdmin();
  const storage = useSelector(selectStorageByID(storageID));

  // The console opens in its own tab, so redux starts empty and the lookups the admin check
  // depends on (`me` plus the roles list it resolves against) arrive asynchronously. Deciding
  // before they land would bounce a legitimate admin straight to /restricted, so hold the page
  // until the role is actually resolvable.
  const me = useSelector(getMe);
  const roles = useSelector((state) => state.lookupData.roles);
  const canResolveRole = Boolean(me) && Array.isArray(roles) && roles.length > 0;

  useEffect(() => {
    if (!storageID || !isAdmin) return;
    dispatch(fetchStorageById(storageID));
  }, [dispatch, storageID, isAdmin]);

  const execClient = useMemo(() => new RemoteExecClient(storageID), [storageID]);

  if (!canResolveRole) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-950 font-mono text-sm text-neutral-500">
        Loading…
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate replace to="/restricted" />;
  }

  const name = storage?.name || storageID;
  const online = Boolean(storage?.online);

  const header = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/10 bg-white/5 px-5 py-3">
      <StatusDot online={online} />
      <span className="font-mono text-sm text-neutral-100">{name}</span>
      <span className="text-xs text-neutral-400">{online ? "Online" : "Offline"}</span>
      <span className="rounded-full bg-amber-400/10 px-2.5 py-0.5 text-xs font-medium text-amber-300 ring-1 ring-amber-400/30">
        Runs as root on the live device
      </span>
    </div>
  );

  return (
    <div className="flex h-screen flex-col gap-3 bg-neutral-950 p-4 sm:p-6">
      <p className="shrink-0 text-xs text-neutral-400">
        Non-interactive — each command runs in a fresh shell, so <code className="font-mono">cd</code> won&apos;t
        persist. <span className="text-neutral-500">↑/↓ for history · Ctrl+L to clear</span>
      </p>
      <div className="min-h-0 flex-1">
        <DeviceShell execClient={execClient} historyScope={storageID} header={header} />
      </div>
    </div>
  );
};
